import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { GUIDE_COUNT, GUIDE_SCHEMA_SAMPLE } from "@/lib/seo-snapshots";

const GuidesIndexContent = lazy(() => import("@/components/pages/GuidesIndexContent").then((m) => ({ default: m.GuidesIndexContent })));

const FAQ = [
  {
    q: "How many Palworld guides are on Pal Stars?",
    a: `There are ${GUIDE_COUNT} guides, and every guide contains exactly five in-depth articles plus its own FAQ, covering progression, breeding, mounts, bosses, base building, farming, the technology tree and the Palworld Official Card Game.`,
  },
  {
    q: "Which Palworld guide should I read first?",
    a: "Start with the Palworld Starter Guide for your first hour, then move to the breeding guide once you unlock the Breeding Farm and the tier list when you begin building an endgame team.",
  },
  {
    q: "Are these guides updated for Palworld 1.0?",
    a: "Yes. Guides are reviewed against current Palworld 1.0 content including Sakurajima and Feybreak, and each guide page shows its last updated date. Card guides follow the printed English card codes from the Palworld Official Card Game.",
  },
  {
    q: "Where do I find the Palworld tier list?",
    a: "The interactive tier list ranks every Pal SS to C for PvE, PvP, base work and mount speed with element and rarity filters, and the Meta Tier List guide explains the reasoning behind each placement.",
  },
];

function faqSchema(items: typeof FAQ, url: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}


function GuidesPending() {
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Guides & Tier Lists</h1>
      <p className="aeo-answer mt-2 max-w-2xl text-muted-foreground">Curated guides for every stage of Palworld, from starter progression to breeding, bosses, base building and the card game.</p>
      <section className="glass rounded-2xl p-6 mt-10">
        <h2 className="text-xl font-bold">Palworld guides FAQ</h2>
        <div className="mt-4 divide-y divide-border/50">
          {FAQ.map((f, i) => (
            <details key={f.q} className="py-3" open={i === 0}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="aeo-answer mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function RouteComponent() {
  return <Suspense fallback={<GuidesPending />}><GuidesIndexContent /></Suspense>;
}

export const Route = createFileRoute("/guides/")({
  validateSearch: (s: Record<string, unknown>): { q?: string } =>
    typeof s.q === "string" && s.q.trim() ? { q: s.q } : {},
  pendingComponent: GuidesPending,
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: "Palworld Guides & Tier Lists — Pal Stars" },
      { name: "description", content: "Palworld guides: progression roadmap, breeding combos, mount speeds, boss strategies, TCG decks and the current meta tier list." },
      { property: "og:title", content: "Palworld Guides & Tier Lists — Pal Stars" },
      { property: "og:description", content: "Progression, breeding, mounts, bosses, TCG strategy and tier lists for Palworld." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/guides" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Guides & Tier Lists — Pal Stars" },
      { name: "twitter:description", content: "Progression, breeding, mounts, bosses, TCG strategy and tier lists for Palworld." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/guides" }],
    scripts: [
      faqSchema(FAQ, "https://palstars.com/guides"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Pal Stars Guides & Tier Lists",
          numberOfItems: GUIDE_COUNT,
          itemListElement: GUIDE_SCHEMA_SAMPLE.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://palstars.com/guides/${g.slug}`,
            name: g.title,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "Guides", item: { "@id": "https://palstars.com/guides", name: "Guides" } },
          ],
        }),
      },
    ],
  }),
});
